import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusCircle, Pencil, Trash2, Search, Loader2, MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { zoneApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ZONE_STATUSES = ["Active", "Inactive"];

// Updated Zone type to match API response
type Zone = {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
};

const ITEMS_PER_PAGE = 10;

const ZonesPage = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    status: boolean;
  }>({
    name: "",
    code: "",
    status: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { toast } = useToast();
  const { user } = useAuth();

  // Load data from API on initial render and when filters/pagination change
  useEffect(() => {
    fetchZones();
  }, [currentPage, filters.query]);

  const fetchZones = async () => {
    try {
      setIsLoading(true);
      const response = await zoneApi.getZones({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query || undefined,
      });

      setZones(response.docs);
      setTotalDocs(response.totalDocs);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch zones",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate total pages based on totalDocs from API
  const totalPages = Math.ceil(totalDocs / ITEMS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === "name") {
      // Capitalize first letter of each word
      const capitalizedValue = (value as string)
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData({ ...formData, [field]: capitalizedValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleAdd = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await zoneApi.createZone({
        ...formData,
        organizationId: user?.organizationId || "",
      });

      fetchZones(); // Refresh the list
      setFormData({ name: "", code: "", status: true });
      setShowForm(false);
      toast({
        title: "Success",
        description: "Zone added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to add zone",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !editingId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await zoneApi.updateZone(editingId, {
        ...formData,
        organizationId: user?.organizationId || "",
      });

      fetchZones(); // Refresh the list
      setFormData({ name: "", code: "", status: true });
      setEditingId(null);
      setShowForm(false);
      toast({
        title: "Success",
        description: "Zone updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to update zone",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (zone: Zone) => {
    setFormData({
      name: zone.name.trim(),
      code: zone.code.trim(),
      status: zone.status,
    });
    setEditingId(zone.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setZoneToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!zoneToDelete) return;

    try {
      setIsLoading(true);
      await zoneApi.deleteZone(zoneToDelete);

      fetchZones(); // Refresh the list
      toast({
        title: "Success",
        description: "Zone deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to delete zone",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setZoneToDelete(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trim fields before submission
    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      code: formData.code.trim(),
    };

    if (!trimmedData.name) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (editingId) {
      handleUpdate();
    } else {
      handleAdd();
    }
  };

  return (
    <MastersLayout
    // title="Zones"
    // description="Manage system zones and their configurations"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search zones..."
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ query: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card>
          <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Zones</CardTitle>
                <CardDescription>
                  Manage system zones and their configurations
                </CardDescription>
              </div>
              <Button onClick={() => setShowForm(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Zone
              </Button>
            </div>
          {/* </CardHeader>
          <CardContent> */}
            <Table>
              <TableHeader>
                <TableRow className="h-10">
                  <TableHead>Name</TableHead>
                  <TableHead style={{ minWidth: "200px" }}>
                    Description
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow className="h-10">
                    <TableCell className="text-center py-4 text-muted-foreground" colSpan={4}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : zones.length === 0 ? (
                  <TableRow className="h-10">
                    <TableCell className="text-center py-4 text-muted-foreground" colSpan={4}>
                      No zones found
                    </TableCell>
                  </TableRow>
                ) : (
                  zones.map((zone) => (
                    <TableRow className="h-10" key={zone.id}>
                      <TableCell className="px-3 py-2 font-medium whitespace-nowrap">
                        {zone.name}
                      </TableCell>
                      <TableCell className="px-3 py-2 w-auto md:w-1/3 lg:w-2/5 overflow-hidden">
                        <div className="break-words whitespace-normal line-clamp-3">
                          {zone.code}
                        </div>
                      </TableCell>{" "}
                      <TableCell className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`status-${zone.id}`}
                            checked={zone.status}
                            onCheckedChange={async (checked) => {
                              try {
                                setIsLoading(true);
                                await zoneApi.updateZone(zone.id, {
                                  ...zone,
                                  status: checked,
                                });
                                fetchZones(); // Refresh the list
                                toast({
                                  title: "Success",
                                  description: `Zone status updated to ${
                                    checked ? "Active" : "Inactive"
                                  }`,
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: error.message
                                    ? error.message
                                    : "Failed to update zone status",
                                  variant: "destructive",
                                });
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                          />
                          <span>{zone.status ? "Active" : "Inactive"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="cursor-pointer">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(zone)} className="cursor-pointer">
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(zone.id)} 
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

            {zones.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          {/* </CardContent>
        </Card> */}
      </div>

      {showForm && (
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            if (!open) {
              setFormData({ name: "", code: "", status: true });
              setEditingId(null);
            }
            setShowForm(open);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit Zone" : "Add New Zone"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the zone information below"
                  : "Fill in the zone information below"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter zone name"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="code">Description</Label>
                <textarea
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value)}
                  placeholder="Enter zone description"
                  // required
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="status"
                    checked={formData.status}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        status: checked,
                      })
                    }
                  />
                  <span>{formData.status ? "Active" : "Inactive"}</span>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ name: "", code: "", status: true });
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Add"} Zone
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure want to delete the zone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MastersLayout>
  );
};

export default ZonesPage;
